import React, { createContext, useContext, useReducer, useCallback } from "react";
import { Product, ProductVariant, PrintDesign } from "@/lib/types";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/firebase/client";
import { generateId } from "@/lib/utils";
import { toastSuccess, toastError } from "@/components/ui/toast";

// Action types for the product state reducer
type ProductAction =
  | { type: "LOAD_PRODUCT"; payload: Product }
  | { type: "UPDATE_PRODUCT"; payload: Partial<Product> }
  | { type: "ADD_VARIANT"; payload: ProductVariant }
  | { type: "UPDATE_VARIANT"; payload: { index: number; variant: Partial<ProductVariant> } }
  | { type: "DELETE_VARIANT"; payload: number }
  // Product-level print designs (NEW)
  | { type: "ADD_PRODUCT_PRINT_DESIGN"; payload: PrintDesign }
  | {
      type: "UPDATE_PRODUCT_PRINT_DESIGN";
      payload: { index: number; design: Partial<PrintDesign> };
    }
  | { type: "DELETE_PRODUCT_PRINT_DESIGN"; payload: number }
  // Variant-level print designs (LEGACY - for backward compatibility)
  | { type: "ADD_PRINT_DESIGN"; payload: { variantIndex: number; design: PrintDesign } }
  | {
      type: "UPDATE_PRINT_DESIGN";
      payload: { variantIndex: number; designIndex: number; design: Partial<PrintDesign> };
    }
  | { type: "DELETE_PRINT_DESIGN"; payload: { variantIndex: number; designIndex: number } }
  | { type: "SET_SAVING"; payload: boolean }
  | { type: "MARK_SAVED" }
  | { type: "RESET_PRODUCT" };

// Product state interface
interface ProductState {
  product: Product | null;
  originalProduct: Product | null; // For change detection
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isLoading: boolean;
}

// Initial state
const initialState: ProductState = {
  product: null,
  originalProduct: null,
  hasUnsavedChanges: false,
  isSaving: false,
  isLoading: false,
};

// Deep comparison helper to detect changes
const deepEqual = (obj1: any, obj2: any): boolean => {
  if (obj1 === obj2) return true;

  if (obj1 == null || obj2 == null) return obj1 === obj2;

  if (typeof obj1 !== typeof obj2) return false;

  if (typeof obj1 !== "object") return obj1 === obj2;

  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false;
    }
    return true;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
};

// Deep clone helper
const deepClone = <T,>(obj: T): T => {
  if (obj === null || typeof obj !== "object") return obj;

  if (obj instanceof Date) return new Date(obj.getTime()) as T;

  if (Array.isArray(obj)) {
    return obj.map(item => deepClone(item)) as T;
  }

  const cloned = {} as T;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }

  return cloned;
};

// Product state reducer
const productStateReducer = (state: ProductState, action: ProductAction): ProductState => {
  switch (action.type) {
    case "LOAD_PRODUCT": {
      const product = action.payload;
      return {
        ...state,
        product,
        originalProduct: deepClone(product),
        hasUnsavedChanges: false,
        isLoading: false,
      };
    }

    case "UPDATE_PRODUCT": {
      if (!state.product) return state;

      const updatedProduct = { ...state.product, ...action.payload };
      const hasChanges = !deepEqual(updatedProduct, state.originalProduct);

      return {
        ...state,
        product: updatedProduct,
        hasUnsavedChanges: hasChanges,
      };
    }

    case "ADD_VARIANT": {
      if (!state.product) return state;

      const updatedProduct = {
        ...state.product,
        variants: [...(state.product.variants || []), action.payload],
      };

      return {
        ...state,
        product: updatedProduct,
        hasUnsavedChanges: !deepEqual(updatedProduct, state.originalProduct),
      };
    }

    case "UPDATE_VARIANT": {
      if (!state.product || !state.product.variants) return state;

      const updatedVariants = [...state.product.variants];
      updatedVariants[action.payload.index] = {
        ...updatedVariants[action.payload.index],
        ...action.payload.variant,
      };

      const updatedProduct = {
        ...state.product,
        variants: updatedVariants,
      };

      return {
        ...state,
        product: updatedProduct,
        hasUnsavedChanges: !deepEqual(updatedProduct, state.originalProduct),
      };
    }

    case "DELETE_VARIANT": {
      if (!state.product || !state.product.variants) return state;

      const updatedVariants = [...state.product.variants];
      updatedVariants.splice(action.payload, 1);

      const updatedProduct = {
        ...state.product,
        variants: updatedVariants,
      };

      return {
        ...state,
        product: updatedProduct,
        hasUnsavedChanges: !deepEqual(updatedProduct, state.originalProduct),
      };
    }

    // Product-level print designs (NEW)
    case "ADD_PRODUCT_PRINT_DESIGN": {
      if (!state.product) return state;

      const updatedProduct = {
        ...state.product,
        printDesigns: [...(state.product.printDesigns || []), action.payload],
      };

      return {
        ...state,
        product: updatedProduct,
        hasUnsavedChanges: !deepEqual(updatedProduct, state.originalProduct),
      };
    }

    case "UPDATE_PRODUCT_PRINT_DESIGN": {
      if (!state.product || !state.product.printDesigns) return state;

      const updatedDesigns = [...state.product.printDesigns];
      updatedDesigns[action.payload.index] = {
        ...updatedDesigns[action.payload.index],
        ...action.payload.design,
      };

      const updatedProduct = {
        ...state.product,
        printDesigns: updatedDesigns,
      };

      return {
        ...state,
        product: updatedProduct,
        hasUnsavedChanges: !deepEqual(updatedProduct, state.originalProduct),
      };
    }

    case "DELETE_PRODUCT_PRINT_DESIGN": {
      if (!state.product || !state.product.printDesigns) return state;

      const updatedDesigns = [...state.product.printDesigns];
      updatedDesigns.splice(action.payload, 1);

      const updatedProduct = {
        ...state.product,
        printDesigns: updatedDesigns,
      };

      return {
        ...state,
        product: updatedProduct,
        hasUnsavedChanges: !deepEqual(updatedProduct, state.originalProduct),
      };
    }

    // Variant-level print designs (LEGACY - for backward compatibility)
    case "ADD_PRINT_DESIGN": {
      if (!state.product || !state.product.variants) return state;

      const updatedVariants = [...state.product.variants];
      const variant = updatedVariants[action.payload.variantIndex];

      updatedVariants[action.payload.variantIndex] = {
        ...variant,
        printDesigns: [...(variant.printDesigns || []), action.payload.design],
      };

      const updatedProduct = {
        ...state.product,
        variants: updatedVariants,
      };

      return {
        ...state,
        product: updatedProduct,
        hasUnsavedChanges: !deepEqual(updatedProduct, state.originalProduct),
      };
    }

    case "UPDATE_PRINT_DESIGN": {
      if (!state.product || !state.product.variants) return state;

      const updatedVariants = [...state.product.variants];
      const variant = updatedVariants[action.payload.variantIndex];
      const updatedDesigns = [...(variant.printDesigns || [])];

      updatedDesigns[action.payload.designIndex] = {
        ...updatedDesigns[action.payload.designIndex],
        ...action.payload.design,
      };

      updatedVariants[action.payload.variantIndex] = {
        ...variant,
        printDesigns: updatedDesigns,
      };

      const updatedProduct = {
        ...state.product,
        variants: updatedVariants,
      };

      return {
        ...state,
        product: updatedProduct,
        hasUnsavedChanges: !deepEqual(updatedProduct, state.originalProduct),
      };
    }

    case "DELETE_PRINT_DESIGN": {
      if (!state.product || !state.product.variants) return state;

      const updatedVariants = [...state.product.variants];
      const variant = updatedVariants[action.payload.variantIndex];
      const updatedDesigns = [...(variant.printDesigns || [])];

      updatedDesigns.splice(action.payload.designIndex, 1);

      updatedVariants[action.payload.variantIndex] = {
        ...variant,
        printDesigns: updatedDesigns,
      };

      const updatedProduct = {
        ...state.product,
        variants: updatedVariants,
      };

      return {
        ...state,
        product: updatedProduct,
        hasUnsavedChanges: !deepEqual(updatedProduct, state.originalProduct),
      };
    }

    case "SET_SAVING": {
      return {
        ...state,
        isSaving: action.payload,
      };
    }

    case "MARK_SAVED": {
      return {
        ...state,
        originalProduct: state.product ? deepClone(state.product) : null,
        hasUnsavedChanges: false,
        isSaving: false,
      };
    }

    case "RESET_PRODUCT": {
      return initialState;
    }

    default:
      return state;
  }
};

// Context interface
interface ProductStateContextType {
  // State
  product: Product | null;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isLoading: boolean;

  // Actions
  loadProduct: (productId: string) => Promise<void>;
  createNewProduct: () => void;
  updateProduct: (updates: Partial<Product>) => void;
  addVariant: (variant: ProductVariant) => void;
  updateVariant: (index: number, variant: Partial<ProductVariant>) => void;
  deleteVariant: (index: number) => void;
  // Product-level print designs (NEW)
  addProductPrintDesign: (design: PrintDesign) => void;
  updateProductPrintDesign: (index: number, design: Partial<PrintDesign>) => void;
  deleteProductPrintDesign: (index: number) => void;
  // Variant-level print designs (LEGACY)
  addPrintDesign: (variantIndex: number, design: PrintDesign) => void;
  updatePrintDesign: (
    variantIndex: number,
    designIndex: number,
    design: Partial<PrintDesign>
  ) => void;
  deletePrintDesign: (variantIndex: number, designIndex: number) => void;
  saveProduct: (overrides?: Partial<Product>) => Promise<boolean>;
  resetProduct: () => void;
  discardChanges: () => void;
}

// Create context
const ProductStateContext = createContext<ProductStateContextType | undefined>(undefined);

// Provider component
export const ProductStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(productStateReducer, initialState);

  // Load product from Firestore
  const loadProduct = useCallback(async (productId: string) => {
    try {
      dispatch({ type: "SET_SAVING", payload: true }); // Use as loading state

      const productRef = doc(db, "products", productId);
      const productSnap = await getDoc(productRef);

      if (productSnap.exists()) {
        const productData = { id: productSnap.id, ...productSnap.data() } as Product;
        dispatch({ type: "LOAD_PRODUCT", payload: productData });
      } else {
        toastError({
          title: "Product Not Found",
          description: "The requested product could not be found.",
        });
      }
    } catch (error) {
      console.error("Error loading product:", error);
      toastError({
        title: "Load Failed",
        description: "Failed to load product data.",
      });
    } finally {
      dispatch({ type: "SET_SAVING", payload: false });
    }
  }, []);

  // Create new product
  const createNewProduct = useCallback(() => {
    const newProduct: Product = {
      id: generateId("prod", 10),
      name: "",
      description: "",
      thumbnail: "",
      categories: [],
      variants: [],
      sortOrder: 999999,
      active: true,
      createdAt: undefined,
    };

    dispatch({ type: "LOAD_PRODUCT", payload: newProduct });
  }, []);

  // Update product
  const updateProduct = useCallback((updates: Partial<Product>) => {
    dispatch({ type: "UPDATE_PRODUCT", payload: updates });
  }, []);

  // Add variant
  const addVariant = useCallback((variant: ProductVariant) => {
    dispatch({ type: "ADD_VARIANT", payload: variant });
  }, []);

  // Update variant
  const updateVariant = useCallback((index: number, variant: Partial<ProductVariant>) => {
    dispatch({ type: "UPDATE_VARIANT", payload: { index, variant } });
  }, []);

  // Delete variant
  const deleteVariant = useCallback((index: number) => {
    dispatch({ type: "DELETE_VARIANT", payload: index });
  }, []);

  // Add print design
  const addPrintDesign = useCallback((variantIndex: number, design: PrintDesign) => {
    dispatch({ type: "ADD_PRINT_DESIGN", payload: { variantIndex, design } });
  }, []);

  // Update print design
  const updatePrintDesign = useCallback(
    (variantIndex: number, designIndex: number, design: Partial<PrintDesign>) => {
      dispatch({ type: "UPDATE_PRINT_DESIGN", payload: { variantIndex, designIndex, design } });
    },
    []
  );

  // Delete print design
  const deletePrintDesign = useCallback((variantIndex: number, designIndex: number) => {
    dispatch({ type: "DELETE_PRINT_DESIGN", payload: { variantIndex, designIndex } });
  }, []);

  // Product-level print designs (NEW)
  const addProductPrintDesign = useCallback((design: PrintDesign) => {
    dispatch({ type: "ADD_PRODUCT_PRINT_DESIGN", payload: design });
  }, []);

  const updateProductPrintDesign = useCallback((index: number, design: Partial<PrintDesign>) => {
    dispatch({ type: "UPDATE_PRODUCT_PRINT_DESIGN", payload: { index, design } });
  }, []);

  const deleteProductPrintDesign = useCallback((index: number) => {
    dispatch({ type: "DELETE_PRODUCT_PRINT_DESIGN", payload: index });
  }, []);

  // Save product to Firestore
  // Accepts optional overrides to merge into product before saving
  const saveProduct = useCallback(
    async (overrides?: Partial<Product>): Promise<boolean> => {
      if (!state.product) {
        toastError({
          title: "Save Failed",
          description: "No product data to save.",
        });
        return false;
      }

      dispatch({ type: "SET_SAVING", payload: true });

      try {
        const productRef = doc(db, "products", state.product.id);

        // Prepare product data for saving, merging in any overrides
        const productData = {
          ...state.product,
          ...overrides, // Apply overrides (e.g., uploaded URLs)
          updatedAt: serverTimestamp(),
          // Set createdAt only if it doesn't exist (new product)
          ...(state.product.createdAt === undefined && { createdAt: serverTimestamp() }),
        };

        await setDoc(productRef, productData);

        dispatch({ type: "MARK_SAVED" });

        toastSuccess({
          title: "Product Saved",
          description: "Product has been successfully saved to the database.",
        });

        return true;
      } catch (error) {
        console.error("Error saving product:", error);
        toastError({
          title: "Save Failed",
          description: "Failed to save product to the database.",
        });
        return false;
      } finally {
        dispatch({ type: "SET_SAVING", payload: false });
      }
    },
    [state.product]
  );

  // Reset product
  const resetProduct = useCallback(() => {
    dispatch({ type: "RESET_PRODUCT" });
  }, []);

  // Discard changes
  const discardChanges = useCallback(() => {
    if (state.originalProduct) {
      dispatch({ type: "LOAD_PRODUCT", payload: state.originalProduct });
    }
  }, [state.originalProduct]);

  const contextValue: ProductStateContextType = {
    // State
    product: state.product,
    hasUnsavedChanges: state.hasUnsavedChanges,
    isSaving: state.isSaving,
    isLoading: state.isLoading,

    // Actions
    loadProduct,
    createNewProduct,
    updateProduct,
    addVariant,
    updateVariant,
    deleteVariant,
    // Product-level print designs (NEW)
    addProductPrintDesign,
    updateProductPrintDesign,
    deleteProductPrintDesign,
    // Variant-level print designs (LEGACY)
    addPrintDesign,
    updatePrintDesign,
    deletePrintDesign,
    saveProduct,
    resetProduct,
    discardChanges,
  };

  return (
    <ProductStateContext.Provider value={contextValue}>{children}</ProductStateContext.Provider>
  );
};

// Hook to use the product state context
export const useProductState = (): ProductStateContextType => {
  const context = useContext(ProductStateContext);
  if (context === undefined) {
    throw new Error("useProductState must be used within a ProductStateProvider");
  }
  return context;
};
